//TODO: https://varomoney.atlassian.net/browse/JT-120935
/* eslint-disable max-lines */
import { gql } from 'urql';

import {
  associatedAccounts,
  IllustrationStyleDictionary,
  institution,
  LaunchPinwheelFlowActionFragment,
  money,
} from './Fragments';
import { AnalyticsFragment } from './VxlFragments';

//
// Supporting Types
//

const card = gql`
  fragment card on Card {
    id
    type
    status
    maskedCardNumber
    name
    isLocked
    isPinSet
    isDigital
    isPhysical
    digital
    actions
    changePinOptions
    thumbnail {
      __typename
      ...IllustrationStyleDictionaryFragment
    }
    illustration {
      __typename
      ...IllustrationStyleDictionaryFragment
    }
    associatedAccountIds
    cardHolderName {
      full
    }
    associatedAccounts {
      ...associatedAccounts
    }
    shippingInformation: shippingInformationV2 {
      arrivalMessage
    }
    __typename
  }
  ${associatedAccounts}
  ${IllustrationStyleDictionary}
`;

export const IllustrationStyleDictionaryFragment = gql`
  fragment IllustrationStyleDictionaryFragment on IllustrationStyleDictionary {
    __typename
    id
    illustrationKey
    fallbackURL
  }
`;

export const TagFragment = gql`
  fragment TagFragment on Tag {
    id
    tagColor
    text
    icon
  }
`;

export const AnalyticsEventFragment = gql`
  fragment AnalyticsEventFragment on AnalyticsEvent {
    __typename
    name
    properties {
      __typename
      key
      value
    }
  }
`;

export const RouteNavigationActionFragment = gql`
  fragment RouteNavigationActionFragment on RouteNavigationAction {
    __typename
    route
    analyticsEvent {
      ...AnalyticsEventFragment
    }
  }
`;

export const InternalAccountTileFragment = gql`
  fragment BannerMessageFragment on IBannerMessageR2 {
    __typename
    id
    type
    title
    message
    ... on RelinkPlaidAccountBannerMessage {
      cta
      relinkType
    }
    ... on ActionableBannerMessages {
      cta
      actionType
    }
    ... on InformationalBannerMessage {
      id
    }
    ... on DebitCardSetPinBannerMessage {
      card {
        __typename
        id
        isPinSet
      }
    }
    ... on SuspiciousCardTransactionBannerMessage {
      transactionId
    }
    ... on InformationalBannerMessageV2 {
      id
      type
      title
      message
      dismissId
      bannerNotificationType
      action {
        __typename
        ... on DeepLinkAction {
          id
          copy
          style
          deeplink
        }
      }
    }
  }

  fragment InternalAccountTileFragment on InternalAccountTile {
    id
    status
    tileType
    internalAccountTileType
    accountId
    displayAnalytics {
      __typename
      ...AnalyticsEventFragment
    }
    clickAnalytics {
      __typename
      ...AnalyticsEventFragment
    }
    tiles {
      id
      status
      tileType
      __typename
      ...ListTileAccountActionsFragment
      ...AccountActionListTileFragment
      ... on CardTrackerAttachmentTile {
        cardTrackerAttachmentTileType
        id
        status
        tileType
        cardId
        title
        route {
          ...RouteNavigationActionFragment
        }
        card {
          __typename
          id
          shippingInformationV2 {
            __typename
            arrivalMessage
          }
          actions
        }
      }
    }
    account {
      __typename
      id
      type
      nickname
      product
      accountStatus: status
      name
      iconUrl
      accountNumberLast4
      accountNumber
      tag {
        __typename
        ...TagFragment
      }
      bannerMessagesR2 {
        __typename
        ...BannerMessageFragment
      }
      ... on InternalAdvanceAccount {
        advanceStatus
        description
        visibility {
          __typename
          isVisible
          userPreference
        }
        activeAdvance {
          __typename
          dueDate
          amountDue {
            __typename
            amount
            formatted
          }
        }
      }
      ... on InternalChargeCardAccount {
        id
        description
        accountNumberLast4
        cards {
          ...card
        }
        action {
          __typename
          type
          title
          location
          message
          cardCta
        }
        vaultBalance {
          __typename
          amount
          currency
        }
      }
      ... on IDisplayBalance {
        displayBalance {
          __typename
          title
          amount {
            __typename
            amount
            formatted
          }
        }
      }
      ... on InternalSavingsAccount {
        yearToDateInterest {
          __typename
          amount
          currency
        }
      }
      ... on InternalCheckingAccount {
        cards {
          ...card
        }
      }
    }
  }
`;

export const QuickActionListFragment = gql`
  fragment QuickActionListFragment on ListTile {
    __typename
    id
    status
    tileType
    listTileType
    tiles {
      __typename
      id
      status
      tileType
      ... on QuickActionTile {
        id
        quickActionTileStatus: status
        tileType
        quickActionTileType
        title
        onPress {
          ...RouteNavigationActionFragment
        }
      }
    }
  }
`;

export const AccountActionTileListFragment = gql`
  fragment AccountActionTileFragment on AccountActionTile {
    __typename
    accountActionTileType
    id
    tileType
    status
    iconV2
    cta
    cardId
    tags {
      __typename
      ...TagFragment
    }
    analyticsEvent {
      __typename
      ...AnalyticsEventFragment
    }
    onClickAccountTile: onClick {
      __typename
    }
    onClickV2 {
      ...RouteNavigationActionFragment
    }
  }

  fragment AccountActionListTileFragment on AccountActionListTile {
    __typename
    id
    status
    tileType
    title
    tiles {
      __typename
      ... on AccountActionTile {
        id
        tileType
        ...AccountActionTileFragment
      }
    }
  }

  fragment ListTileAccountActionsFragment on ListTile {
    __typename
    id
    status
    tileType
    listTileType
    listTitle
    tiles {
      __typename
      ... on AccountActionTile {
        id
        tileType
        ...AccountActionTileFragment
      }
    }
  }
`;

export const LinkedAccountsTileFragment = gql`
  fragment LinkedAccountsTileFragment on LinkedAccountsTile {
    __typename
    id
    status
    tileType
    title
    tiles {
      __typename
      id
      status
      tileType
      id
      ... on ExternalAccountsTile {
        externalAccounts {
          ... on ExternalPlaidAccount {
            id
            name
            iconUrl
            nickname
            institution {
              ...institution
            }
            status
            accountNumberLast4
            accountTypeLast2
            lastUpdatedCopy
            displayBalance {
              title
              amount {
                ...money
              }
            }
          }
        }
      }
    }
  }
  ${money}
  ${institution}
`;

export const PromotionTileFragment = gql`
  fragment PromotionTileFragment on PromotionTile {
    __typename
    id
    status
    tileType
    promotionTileType
    title
    description
    icon
    rightImage
    promotionTileCta: cta
  }
`;

export const ProductOfferSingleCardTileFragment = gql`
  fragment ProductOfferSingleCardTileFragment on ProductOfferSingleCardTile {
    __typename
    id
    status
    tileType
    productOfferCard: card {
      __typename
      ... on ProductOfferCard {
        ...ProductOfferCardFragment
      }
    }
  }

  fragment ProductOfferCardFragment on ProductOfferCard {
    __typename
    id
    titleText
    bodyText
    actionTextV2
    illustration {
      ...IllustrationStyleDictionaryFragment
    }
    onClickV2 {
      __typename
      ... on RouteNavigationAction {
        route
        __typename
        analyticsEvent {
          __typename
          ...AnalyticsEventFragment
        }
      }
    }
    illustrationKey
    imageBackgroundColor
  }
`;

export const BannerPromotionTileFragment = gql`
  ${AnalyticsFragment}
  fragment BannerPromotionTileFragment on BannerPromotionTile {
    __typename
    id
    status
    tileType
    bannerPromotionTileType
    heroText
    image {
      id
      fallbackURL
    }
    contextText
    onClickAnalytics {
      ...analytics
    }
    tileId
  }
`;

const CreditScoreResultFragment = gql`
  fragment CreditScoreResultFragment on CreditScoreResult {
    __typename
    ... on CreditScoreSuccess {
      ranges {
        name
        description
        min
        max
        __typename
      }
      description
      ranges {
        name
        description
        min
        max
      }
      factors {
        name
        illustration
        value
      }
      grade
      gradeEnum
      lastUpdated
      providerLogo
      score
      scoreDelta
    }

    ... on CreditScoreNoScore {
      description
      grade
      gradeEnum
      score
      ranges {
        name
        description
        min
        max
      }
    }
    ... on HideCreditScore {
      shouldHideCreditScore
    }
  }
`;

const OnboardingCardFragment = gql`
  ${RouteNavigationActionFragment}
  ${LaunchPinwheelFlowActionFragment}
  fragment OnboardingCardFragment on OnboardingCard {
    __typename
    actionText
    bodyText
    illustration
    actionV2 {
      __typename
      ...RouteNavigationActionFragment
      ...LaunchPinwheelFlowActionFragment
    }
  }
`;

const WalletOnboardingCardFragment = gql`
  ${card}
  fragment WalletOnboardingCardFragment on WalletOnboardingCard {
    __typename
    id
    actionId
    bodyText
    illustration
    cardId
    card {
      ...card
    }
  }
`;

export const NextBestActionTileFragment = gql`
  ${OnboardingCardFragment}
  ${WalletOnboardingCardFragment}
  fragment NextBestActionTileFragment on NextBestActionTile {
    __typename
    id
    status
    tileType
    layout
    onboardingCardsV2 {
      ...OnboardingCardFragment
      ...WalletOnboardingCardFragment
    }
  }
`;

const V2ATransferActionFragment = gql`
  fragment IV2ARecipientFragment on IV2ARecipient {
    ... on V2AExternalRecipient {
      __typename
      id
      displayName
      phoneNumber
      email {
        address
      }
    }
    ... on V2AInternalRecipient {
      __typename
      id
      displayName
      phoneNumber
      email {
        address
      }
    }
    ... on V2ARecipientDetailNotFound {
      __typename
      id
      displayName
      phoneNumber
      email {
        address
      }
      errorMessage
    }
  }
  fragment V2ATransferActionFragment on V2ATransferAction {
    recipient {
      ...IV2ARecipientFragment
    }
    analyticsEvent {
      __typename
      ...AnalyticsEventFragment
    }
  }
`;

export const VaroToAnyoneTileFragment = gql`
  ${V2ATransferActionFragment}
  fragment VaroToAnyoneTileFragment on VaroToAnyoneTile {
    __typename
    id
    tileId
    status
    tileType
    staticActionCopy
    staticActionAnalytics {
      __typename
      ...AnalyticsEventFragment
    }
    topRecipients {
      ...V2ATransferActionFragment
    }
  }
`;

export const SystemBannerFragment = gql`
  fragment SystemBannerFragment on VXLSystemBanner {
    __typename
    id
    dismissId
    title
    body
    type
    iconV2
    type
    alertName
    bannerNavigation {
      __typename
      route
      analyticsEvent {
        ...AnalyticsEventFragment
      }
    }
    buttonTitle
    buttonAction {
      route
      analyticsEvent {
        ...AnalyticsEventFragment
      }
    }
  }
`;

export const CreditScoreTileFragment = gql`
  ${CreditScoreResultFragment}
  fragment CreditScoreTileFragment on CreditScoreTile {
    __typename
    creditScore {
      ...CreditScoreResultFragment
    }
    creditScoreNudgeMessage {
      clickAnalytics {
        __typename
        ...AnalyticsEventFragment
      }
      displayAnalytics {
        __typename
        ...AnalyticsEventFragment
      }
      isActionable
      message
      nudgeMessageType
      title
    }
    id
    status
    tileId
    tileType
    creditScoreOnClickAnalytics: onClickAnalytics {
      __typename
      ...AnalyticsEventFragment
    }
  }
`;

export const ProductOfferTileFragment = gql`
  fragment ProductOfferTileFragment on ProductOfferTile {
    __typename
    id
    status
    tileType
    productOfferTileType
    title
    description
    productOfferTileCta: cta
    clickAnalytics {
      ...AnalyticsEventFragment
    }
    application {
      __typename
      id
      ... on ChargeCardApplication {
        __typename
        chargeCardApplicationStatus
      }
    }
    clickAnalytics {
      ...AnalyticsEventFragment
    }
  }
`;

export const VXLAccountCardSquareFragment = gql`
  fragment VXLAccountCardSquareFragment on VXLAccountCardSquare {
    __typename
    amountText
    bodyText
    captionText
    isDisabled
    showPlusIcon
    icon
    tag {
      __typename
      ...TagFragment
    }
    titleText
    onPress {
      ...RouteNavigationActionFragment
    }
  }
`;

export const PayOverTimeItemsTileFragment = gql`
  fragment PayOverTimeItemsTileFragment on PayOverTimeItemsTile {
    __typename
    id
    status
    tileType
    items {
      __typename
      id
      state
      title
      icon
      balance {
        __typename
        formatted
      }
      caption
      tag {
        __typename
        ...TagFragment
      }
      onPress {
        ...RouteNavigationActionFragment
      }
    }
  }
`;

export const VXLSideBySideTileFragment = gql`
  ${VXLAccountCardSquareFragment}

  fragment VXLSideBySideTileFragment on VXLSideBySideTile {
    __typename
    id
    status
    tileType
    tiles {
      __typename
      id
      status
      tileType
      ... on VLOCAccountCardSquareTile {
        card {
          ...VXLAccountCardSquareFragment
        }
      }
      ... on IAccountCardSquareTile {
        card {
          ...VXLAccountCardSquareFragment
        }
      }
    }
  }
`;

export const TopPerksTileFragment = gql`
  fragment TopPerksTileFragment on TopPerksTile {
    __typename
    id
    status
    tileType
    topPicks {
      __typename
      id
      name
      industry
      shortDescription
      cashbackDescription
      cashbackAmount
      offerType
      logoImage
      clickAnalytics {
        ...AnalyticsEventFragment
      }
    }
  }
`;

export const PartnerOfferTileFragment = gql`
  fragment PartnerOfferTileFragment on PartnerOfferTile {
    __typename
    id
    status
    tileType
    partnerOfferId
    title
    cta
    description
    icon
    rightImage
  }
`;

export const VXLTrackerCardTileFragment = gql`
  fragment VXLTrackerCardTileFragment on VXLTrackerCardTile {
    __typename
    id
    status
    tileType
    trackerCard: card {
      __typename
      ... on VXLTrackerCard {
        titleText
        onPress {
          ...RouteNavigationActionFragment
        }
        trackerProps {
          __typename
          fillColor
          backgroundColor
          backgroundTextureV2
          progressValue {
            __typename
            ... on FloatType {
              __typename
              float
            }
          }
        }
        leadingLabel
        leadingText
        nudge {
          __typename
          titleText
          bodyText
          showsChevron
          buttonText
          icon
          onPress {
            ...RouteNavigationActionFragment
          }
        }
        trailingText
        trailingLabel
      }
    }
  }
`;

export const ColumnTaxTileFragment = gql`
  fragment ColumnTaxTileFragment on ColumnTaxTile {
    __typename
    id
    status
    tileType
    headerText
    headerTag {
      __typename
      ...TagFragment
    }
    bodyTitleText
    bodyTitleTrailingText
    bodyDescriptionText
    columnTaxRows {
      __typename
      id
      ColumnTaxRowStatus
      leadingText
      trailingText
    }
    footerButtonCopy
    footerButtonAction {
      __typename
      ...RouteNavigationActionFragment
    }
  }
`;

export const SelfFundedOfferTileFragment = gql`
  fragment SelfFundedOfferTileFragment on SelfFundedOfferTile {
    __typename
    id
    status
    tileType
    selfFundedOfferCard {
      __typename
      ... on PromotionalSelfFundedOffer {
        id
        iconUrls
        title
        tag {
          __typename
          ...TagFragment
        }
        footerButton {
          __typename
          id
          buttonText
          buttonStyle
          buttonOnClick {
            __typename
            ... on RouteNavigationAction {
              __typename
              route
              analyticsEvent {
                __typename
                ...AnalyticsEventFragment
              }
            }
          }
        }
      }
      ... on ActivatedSelfFundedOffer {
        __typename
        offerId
        avatarUrl
        title
        subTitle
        tag {
          __typename
          ...TagFragment
        }
        onClick {
          __typename
          route
          analyticsEvent {
            __typename
            ...AnalyticsEventFragment
          }
        }
      }
    }
  }
`;

export const CheckListItemTileFragment = gql`
  fragment CheckListItemTileFragment on ListItemTile {
    __typename
    id
    status
    tileType
    title
    caption
    state
    toggleToken
  }
`;

export const SDVerticalStackContainerTileFragment = gql`
  fragment SDVerticalStackContainerTileFragment on SDVerticalStackContainerTile {
    __typename
    id
    status
    tileType
    tiles {
      __typename
      id
      status
      tileType
      ... on FullWidthTextComponentTile {
        __typename
        id
        status
        tileType
        text {
          __typename
          copy
          style {
            fontStyle
            color
          }
        }
        textAlignment
      }
      ...CreditScoreTileFragment
      ...VXLTrackerCardTileFragment
    }
  }
`;

export const DiscoverMoreProductRowTileFragment = gql`
  fragment DiscoverMoreProductRowTileFragment on DiscoverMoreProductRowTile {
    productType
    title
    description
    onClickAnalytics {
      ...AnalyticsEventFragment
    }
  }
`;

export const VXLCardGroupDiscoverMoreTileFragment = gql`
  fragment VXLCardGroupDiscoverMoreTileFragment on VXLCardGroupDiscoverMoreTile {
    title
    displayAnalytics {
      ...AnalyticsEventFragment
    }
    discoverTiles: tiles {
      __typename
      id
      status
      tileType
      ...DiscoverMoreProductRowTileFragment
    }
    viewMoreButton {
      label
      onClickAnalytics {
        ...AnalyticsEventFragment
      }
    }
  }
`;
